import { createElement, addEventListeners } from "../utils/dom.js"
import { applyCSSVariables } from "../styles/index.js"
import { createButton } from "./button.js"
import { createModal } from "./modal.js"

/**
 * Create the main feedback widget
 * @param {Object} config - Widget configuration
 * @returns {Object} Widget API
 */
export function createWidget(config) {
  const state = {
    isOpen: false,
    modal: null,
    clickOutsideHandler: null,
    isToggling: false,
  }

  const container = createWidgetContainer(config)
  const button = createButton(config)

  container.appendChild(button)

  // Handle button click
  addEventListeners(button, {
    click: (e) => handleButtonClick(e, container, state, config),
  })

  return {
    element: container,
    destroy: () => destroyWidget(container, state),
  }
}

/**
 * Create the widget container element
 * @param {Object} config - Widget configuration
 * @returns {HTMLElement} Container element
 */
function createWidgetContainer(config) {
  const widget = createElement(
    "div",
    `feedback-widget position-${config.position} theme-${config.theme} style-${config.widgetStyle}`,
  )

  applyCSSVariables(widget, config)
  return widget
}

/**
 * Handle button click events
 * @param {Event} e - Click event
 * @param {HTMLElement} container - Widget container
 * @param {Object} state - Widget state
 * @param {Object} config - Widget configuration
 */
function handleButtonClick(e, container, state, config) {
  e.preventDefault()
  e.stopPropagation()

  // Prevent rapid clicking
  if (state.isToggling) return

  state.isToggling = true

  if (state.isOpen) {
    closeModal(state)
  } else {
    openModal(container, state, config)
  }

  // Reset toggle flag after animation
  setTimeout(() => {
    state.isToggling = false
  }, 350)
}

/**
 * Open the feedback modal
 * @param {HTMLElement} container - Widget container
 * @param {Object} state - Widget state
 * @param {Object} config - Widget configuration
 */
function openModal(container, state, config) {
  if (state.isOpen) return

  state.isOpen = true
  state.modal = createModal(config, () => closeModal(state))
  container.appendChild(state.modal.element)

  // Focus management
  setTimeout(() => {
    const textarea = state.modal.element.querySelector("textarea")
    if (textarea) textarea.focus()
  }, 100)
}

/**
 * Close the feedback modal
 * @param {Object} state - Widget state
 */
function closeModal(state) {
  if (!state.isOpen || !state.modal) return

  // Remove click outside handler if it exists
  if (state.clickOutsideHandler) {
    document.removeEventListener("click", state.clickOutsideHandler)
    state.clickOutsideHandler = null
  }

  state.modal.close()

  setTimeout(() => {
    if (state.modal) {
      state.modal.element.remove()
      state.modal = null
    }
    state.isOpen = false
  }, 300)
}

/**
 * Destroy the widget and clean up resources
 * @param {HTMLElement} container - Widget container
 * @param {Object} state - Widget state
 */
function destroyWidget(container, state) {
  if (state.clickOutsideHandler) {
    document.removeEventListener("click", state.clickOutsideHandler)
  }

  if (container && container.parentNode) {
    container.parentNode.removeChild(container)
  }
}
